import { gql } from "@apollo/client";
import { client } from "../apolloClient";
import { useQuery, useMutation } from "@apollo/client";

/**
 * useRoleCategoryApi custom hook for role category operations.
 * - Provides CRUD operations for role categories using Apollo Client and GraphQL.
 * - Handles cache updates for role category queries and mutations.
 * - Exposes helpers for reading/writing/updating role categories in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting role categories.
 * - Supports fetching by role category ID.
 * - Handles optimistic UI updates and error logging.
 */

// GraphQL Queries and Mutations
const ROLES_CATEGORY_QUERIES = {
    GET_ROLES_CATEGORY: gql`
    query GetAllRoleCategories {
      GetAllRoleCategories {
        role_category_id
        role_cat_name
        is_active
        created_on
        updated_on
      }
    }
    `,
    GET_ROLES_CATEGORY_BY_ID: gql`
    query GetRoleCategory($roleCategoryId: Int!) {
      GetRoleCategory(role_category_id: $roleCategoryId) {
        role_category_id
        role_cat_name
        is_active
        created_on
        updated_on
      }
    }
    `,
    ADD_ROLE_CATEGORY: gql`
    mutation AddRoleCategory($roleCatName: String!) {
      AddRoleCategory(role_cat_name: $roleCatName) {
        role_category_id
        role_cat_name
        is_active
        created_on
        updated_on
      }
    }
    `,
    UPDATE_ROLE_CATEGORY: gql`
    mutation UpdateRoleCategory($roleCategoryId: Int!, $roleCatName: String) {
      UpdateRoleCategory(role_category_id: $roleCategoryId, role_cat_name: $roleCatName) {
        role_category_id
        role_cat_name
        is_active
        created_on
        updated_on
      }
    }
    `,
    DELETE_ROLE_CATEGORY: gql`
    mutation DeleteRoleCategory($roleCategoryId: Int!) {
      DeleteRoleCategory(role_category_id: $roleCategoryId) {
        role_category_id
        role_cat_name
        is_active
        created_on
        updated_on
      }
    }
    `,
};

// Cache operations for role categories
const cacheOperations = {
    /**
     * Writes role categories to Apollo cache.
     * @param {Array} roleCategories
     */
    writeRolesCategories: (roleCategories) => {
        if (!roleCategories) return;
        client.writeQuery({
            query: ROLES_CATEGORY_QUERIES.GET_ROLES_CATEGORY,
            data: {
                GetAllRoleCategories: roleCategories,
            },
        });
    },

    /**
     * Reads role categories from Apollo cache.
     * @returns {Array}
     */
    readRolesCategories: () => {
        try {
            const { GetAllRoleCategories } =
                client.readQuery({
                    query: ROLES_CATEGORY_QUERIES.GET_ROLES_CATEGORY,
                }) || {};
            return GetAllRoleCategories || [];
        } catch {
            return [];
        }
    },

    /**
     * Updates a role category in Apollo cache.
     * @param {Object} updatedRoleCategory
     */
    updateClientInCache: (updatedRoleCategory) => {
        const existingRoleCategories = cacheOperations.readRolesCategories();
        const updatedRoleCategories = existingRoleCategories.map((roleCategory) =>
            roleCategory.role_category_id === updatedRoleCategory.role_category_id ? updatedRoleCategory : roleCategory
        );
        cacheOperations.writeRolesCategories(updatedRoleCategories);
    },
};

export const useRoleCategoryApi = () => {
    const [addRoleCategoryMutation] = useMutation(ROLES_CATEGORY_QUERIES.ADD_ROLE_CATEGORY);
    const [updateRoleCategoryMutation] = useMutation(ROLES_CATEGORY_QUERIES.UPDATE_ROLE_CATEGORY);

    /**
     * Fetches all role categories.
     * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
     */
    const getRolesCategory = async () => {
        try {
            const { data } = await client.query({
                query: ROLES_CATEGORY_QUERIES.GET_ROLES_CATEGORY,
                fetchPolicy: "network-only",
            });
            return { success: true, data: data.GetAllRoleCategories };
        } catch (error) {
            console.error("Error fetching role categories:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Fetches a role category by its ID.
     * @param {number|string} roleCategoryId
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const getRoleCategoryById = async (roleCategoryId) => {
        try {
            const { data } = await client.query({
                query: ROLES_CATEGORY_QUERIES.GET_ROLES_CATEGORY_BY_ID,
                variables: { roleCategoryId: parseInt(roleCategoryId) },
                fetchPolicy: "network-only",
            });
            return { success: true, data: data.GetRoleCategory };
        } catch (error) {
            console.error("Error fetching role category:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Creates a new role category.
     * @param {Object} formData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const createRoleCategory = async (formData) => {
        try {
            const { data, errors } = await addRoleCategoryMutation({
                variables: {
                    roleCatName: formData.role_cat_name,
                    // isActive: formData.is_active
                },
            });

            if (errors?.length > 0) {
                throw new Error(errors[0].message);
            }

            if (!data?.AddRoleCategory) {
                throw new Error("Failed to create role category");
            }

            return {
                success: true,
                data: data.AddRoleCategory,
            };
        } catch (error) {
            // Handle GraphQL errors
            if (error.graphQLErrors?.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                throw new Error(graphQLError.message);
            }

            // Handle network errors
            if (error.networkError?.result?.errors?.length > 0) {
                const networkError = error.networkError.result.errors[0];
                throw new Error(networkError.message);
            }

            throw error;
        }
    };

    /**
     * Updates an existing role category.
     * @param {number|string} roleCategoryId
     * @param {Object} formData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const updateRoleCategory = async (roleCategoryId, formData) => {
        try {
            const { data, errors } = await updateRoleCategoryMutation({
                variables: {
                    roleCategoryId: parseInt(roleCategoryId),
                    roleCatName: formData.role_cat_name,
                    // isActive: formData.is_active,
                },
            });
            if (errors?.length > 0) {
                throw new Error(errors[0].message);
            }
            if (!data?.UpdateRoleCategory) {
                throw new Error("Failed to update role category");
            }

            return {
                success: true,
                data: data.UpdateRoleCategory,
            };
        } catch (error) {
            // Handle GraphQL errors
            if (error.graphQLErrors?.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                throw new Error(graphQLError.message);
            }

            // Handle network errors
            if (error.networkError?.result?.errors?.length > 0) {
                const networkError = error.networkError.result.errors[0];
                throw new Error(networkError.message);
            }

            throw error;
        }
    };

    /**
     * Deletes a role category by its ID.
     * @param {number|string} role_category_id
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const deleteRoleCategory = async (role_category_id) => {
        try {
            const { data } = await client.mutate({
                mutation: ROLES_CATEGORY_QUERIES.DELETE_ROLE_CATEGORY,
                variables: { roleCategoryId: parseInt(role_category_id) },
            });

            const existingCategories = cacheOperations.readRolesCategories();
            cacheOperations.writeRolesCategories(
                existingCategories.filter((role_category) => role_category.role_category_id !== role_category_id)
            );

            return { success: true, data: data.DeleteRoleCategory };
        } catch (error) {
            console.error("Error deleting role category:", error);
            return { success: false, error: error.message };
        }
    };

    return {
        getRolesCategory,
        getRoleCategoryById,
        createRoleCategory,
        updateRoleCategory,
        deleteRoleCategory,
        cacheOperations,
    };
};
